import {
  UseMutateFunction,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  UpdateFormConfigDto,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_FORMS_CONFIG } from "../domain";
import { ACTION_GET_FORMS_CONFIGS } from "../GetFormConfigs/useGetFormConfigs";

import { formsConfigApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_UPDATE_FORM_CONFIG = "UPDATE_FORM_CONFIG";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

type UpdateFormConfigProps = {
  formId: string;
  formConfig: UpdateFormConfigDto;
};

const updateFormConfig = async (props: UpdateFormConfigProps): Promise<void> => {
  const response = await formsConfigApi.formsConfigControllerUpdateFormConfig(
    props.formId,
    props.formConfig,
  );

  return response.data;
};

export const useUpdateFormConfig = (
  user: UserProfileDto | null,
): {
  updateFormConfig: UseMutateFunction<void, Error, UpdateFormConfigProps, unknown>;
  updateFormConfigAsync: (props: UpdateFormConfigProps) => Promise<void>;
  updateFormConfigEnabled: boolean;
  isSuccessUpdateFormConfig: boolean;
  isPendingUpdateFormConfig: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_FORMS_CONFIG, ACTION_UPDATE_FORM_CONFIG],
    mutationFn: enabled ? updateFormConfig : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_FORMS_CONFIG, ACTION_GET_FORMS_CONFIGS],
      });
    },
  });

  return {
    updateFormConfig: mutate,
    updateFormConfigAsync: mutateAsync,
    updateFormConfigEnabled: enabled,
    isSuccessUpdateFormConfig: isSuccess,
    isPendingUpdateFormConfig: isPending,
  };
};
